import { useEffect, useState, useCallback, useRef } from 'react';
import {
  useParams,
  useNavigate,
  generatePath,
  useLocation,
} from 'react-router-dom';
import queryString from 'query-string';
import { ButtonPseudo, ButtonPrimary, SidePanel, SidePanelType } from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { clearDocumentFormState } from '../store/slices/documents/create';
import {
  setNeedToReloadCategoryId,
  clearCursor,
} from '../store/slices/documents/documents';
import DocumentCreate from '../../../components/partial/documents/create';
import {
  useAddCustomerDocumentsMutation,
  useLazyGetCustomerDocumentsQuery,
  useLazyGetCustomerOrganizationDocumentsStatsQuery,
} from '../store/services/customers';
import { CustomerType, CustomerDocumentType } from '../types';
import { useCustomersUploadMutation } from '../../../store/documents/services';
import { useCheckDocumentStringNameMutation } from '../../../../../../store/service';
import { UploadResponse } from '../../../store/documents/types';

import ROUTES from '../../../routes';

const DocumentCreateHOC = ({
  customerType,
}: {
  customerType: CustomerType;
}) => {
  const dropZoneInputRef = useRef<HTMLInputElement | null>(null);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<
    { name: string; id: string }[]
  >([]);
  const [selectedGroup, setSelectedGroup] = useState<string>();
  const [uploadedFilesErrors, setUploadedFilesError] = useState<string[]>([]);
  const [expirationDate, setExpirationDate] = useState<Date | undefined>(
    undefined,
  );
  const [attachedDocumentsErrors, setAttachedDocumentsErrors] = useState<
    Record<string, string> | undefined
  >(undefined);
  const [attachedFiles, setAttachedFiles] = useState<string[]>([]);
  const [uploadedDocumentsErrors, setUploadedDocumentsErrors] =
    useState<{ name: string; error: string }[]>();

  const [getCustomerDocuments] = useLazyGetCustomerDocumentsQuery();
  const [getStats] = useLazyGetCustomerOrganizationDocumentsStatsQuery();

  const {
    customerDocuments: { allowedCategories },
  } = useAppSelector((state) => state);

  const [upload, { isLoading: fileUploadInProgress }] =
    useCustomersUploadMutation();
  const [addCustomerDocument, { isLoading: documentSubmitInProgress }] =
    useAddCustomerDocumentsMutation();
  const [
    checkDocumentStringName,
    { isLoading: checkDocumentStringNameInProgress },
  ] = useCheckDocumentStringNameMutation();

  const { customerId } = useParams();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();

  let link: string;

  if (customerType === CustomerType.INDIVIDUAL) {
    link = generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_DOCUMENTS, {
      customerId: customerId as string,
    });
  } else {
    link = generatePath(ROUTES.CUSTOMERS_CORPORATE_DOCUMENTS, {
      customerId: customerId as string,
    });
  }

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);
    setUploadedFilesError([]);
    setUploadedFiles([]);
    setAttachedDocumentsErrors(undefined);
    setAttachedFiles([]);

    setTimeout(() => {
      navigate(`${link}${search || ''}`);
    }, 100);
  }, []);

  const onSubmitDocuments = async () => {
    setAttachedDocumentsErrors(undefined);

    const otherCategory = allowedCategories?.find(
      (item) => item.category === CustomerDocumentType.OTHER,
    );

    const nextAttachedDocumentsErrors = { ...attachedDocumentsErrors };

    const nextAttachedFiles = ([] as string[]).concat(attachedFiles);

    const responses = await Promise.all(
      uploadedFiles
        ?.filter((item) => nextAttachedFiles?.every((id) => item.id !== id))
        .map(async (item) => {
          const body: {
            uploadId: string;
            name: string;
            documentCategoryId?: string;
            expirationDate?: string;
          } = {
            uploadId: item.id,
            name: item.name,
          };

          if (expirationDate) {
            body.expirationDate = expirationDate.toISOString();
          }

          if (selectedGroup) {
            body.documentCategoryId = selectedGroup;
          } else {
            body.documentCategoryId = otherCategory?.id;
          }

          const response = (await addCustomerDocument({
            id: customerId as string,
            body,
          })) as {
            error?: {
              data: {
                message?: {
                  constraints: Record<string, string>;
                }[];
              };
            };
          };

          if (response.error && response?.error?.data?.message?.length) {
            const errorItem = response?.error?.data?.message[0].constraints;

            if (errorItem) {
              const [documentError] = Object.values(errorItem);

              nextAttachedDocumentsErrors[item.id] = documentError;
            }
          } else {
            nextAttachedFiles.push(item.id);
          }

          return response;
        }),
    );

    setAttachedFiles(nextAttachedFiles);
    setAttachedDocumentsErrors(nextAttachedDocumentsErrors);

    if (responses && responses.length) {
      getStats(customerId as string);

      if (!responses?.find((item) => item.error)) {
        handleClose();
      }

      if (search) {
        const parsed = queryString.parse(search);

        if (parsed) {
          const categoryId = parsed.documentCategoryId as string;

          const body: {
            id: string;
            sort: string;
            categoryId?: string;
            take: string;
            reload?: boolean;
            sortProp: string;
          } = {
            id: customerId as string,
            categoryId: categoryId === 'all' ? '' : categoryId,
            take: '20',
            sort: parsed.sort as string,
            sortProp: parsed.sortProp as string,
          };

          if (
            (selectedGroup && selectedGroup === categoryId) ||
            (!selectedGroup && categoryId === otherCategory?.id) ||
            categoryId === 'all'
          ) {
            body.reload = true;

            await getCustomerDocuments({
              ...body,
              reload: true,
            });

            if (categoryId === 'all') {
              dispatch(clearCursor());
            }
          } else {
            dispatch(setNeedToReloadCategoryId(categoryId));
          }
        }
      }
    }
  };

  const onUpload = async (files: FileList) => {
    setUploadedFilesError([]);
    setUploadedDocumentsErrors([]);

    const nextUploadedFilesErrors: string[] = [];
    const nextUploadedDocumentsErrors: { name: string; error: string }[] = [];

    const responseFiles = await Promise.all(
      Object.keys(files).map(async (file) => {
        const checkNameResponse = (await checkDocumentStringName({
          value: files[file as any].name,
        })) as {
          error?: {
            data: {
              message?: {
                constraints: Record<string, string>;
              }[];
            };
          };
        };

        let nextFile;

        if (!checkNameResponse?.error) {
          const response = (await upload(files[file as any])) as unknown as {
            data?: UploadResponse[];
            error?: {
              originalStatus: number;
              data?: {
                message?: string;
              };
            };
            message?: string;
          };

          if (response && response.data) {
            nextFile = { ...response.data[0] };

            let fileName = files[file as any]?.name;

            if (
              response.data[0] &&
              response.data[0]?.mimeType?.includes('image')
            ) {
              fileName = `${files[file as any]?.name.slice(0, files[file as any]?.name.lastIndexOf('.'))}.jpg`;
            }

            nextFile.name = fileName;
          } else if (response.error) {
            if (response.error.originalStatus === 413) {
              nextUploadedFilesErrors.push(
                `${files[file as any].name} size too large`,
              );
            } else {
              // eslint-disable-next-line no-lonely-if
              if (response?.error?.data?.message) {
                nextUploadedFilesErrors.push(response?.error?.data?.message);
              }
            }
          }
        } else if (
          checkNameResponse?.error &&
          checkNameResponse?.error?.data?.message
        ) {
          const errorItem =
            checkNameResponse?.error?.data?.message[0]?.constraints;

          if (errorItem) {
            nextUploadedDocumentsErrors.push({
              name: files[file as any].name,
              error: Object.values(errorItem)[0],
            });
          }
        }

        return nextFile;
      }),
    );

    setUploadedDocumentsErrors(nextUploadedDocumentsErrors);

    setUploadedFilesError(nextUploadedFilesErrors);

    if (responseFiles && responseFiles.length) {
      const nextUploadedFiles = ([] as { name: string; id: string }[]).concat(
        uploadedFiles,
      );

      responseFiles.forEach((item) => {
        if (item && item.name && item.id) {
          nextUploadedFiles.push({
            name: item.name,
            id: item.id,
          });
        }
      });

      setUploadedFiles(nextUploadedFiles);
    }

    if (uploadInputRef?.current) {
      uploadInputRef.current.value = '';
    }
    if (dropZoneInputRef?.current) {
      dropZoneInputRef.current.value = '';
    }
  };

  const onDelete = (id: string) => {
    if (uploadInputRef?.current) {
      uploadInputRef.current.value = '';
    }

    if (dropZoneInputRef?.current) {
      dropZoneInputRef.current.value = '';
    }

    setUploadedFiles(uploadedFiles.filter((item) => item.id !== id));

    const nextAttachedDocumentsErrors = { ...attachedDocumentsErrors };

    if (nextAttachedDocumentsErrors[id]) {
      delete nextAttachedDocumentsErrors[id];

      setAttachedDocumentsErrors(nextAttachedDocumentsErrors);
    }
  };

  useEffect(() => {
    setPanelIsOpen(true);

    return () => {
      dispatch(clearDocumentFormState());
    };
  }, [dispatch]);

  return (
    <SidePanel
      type={SidePanelType.Accounts}
      closeOnClickOutside
      isOpen={panelIsOpen}
      onClose={handleClose}
      title="Add document"
      sidepanelExpandedKey="customer-create-document"
      footer={
        <>
          <ButtonPrimary
            text={documentSubmitInProgress ? 'Loading...' : 'Add document'}
            disabled={!uploadedFiles.length || documentSubmitInProgress}
            onClick={onSubmitDocuments}
            bold
          />
          <ButtonPseudo text="Close" onClick={handleClose} bold />
        </>
      }
    >
      <DocumentCreate
        onUpload={onUpload}
        onSelect={setSelectedGroup}
        fileUploadInProgress={
          checkDocumentStringNameInProgress || fileUploadInProgress
        }
        uploadInputRef={uploadInputRef}
        dropZoneInputRef={dropZoneInputRef}
        onDelete={onDelete}
        uploadedFiles={uploadedFiles}
        uploadedFilesErrors={uploadedFilesErrors}
        selectedGroup={selectedGroup}
        setExpirationDate={setExpirationDate}
        expirationDate={expirationDate}
        allowedCategories={allowedCategories}
        setUploadedFiles={setUploadedFiles}
        attachedDocumentsErrors={attachedDocumentsErrors}
        attachedFiles={attachedFiles}
        uploadedDocumentsErrors={uploadedDocumentsErrors}
      />
    </SidePanel>
  );
};

export default DocumentCreateHOC;
